/**
 * `<ColorSelect>` — the one control for choosing a colour in this app.
 *
 * It lived in `IndicatorPicker` until the annotation style bar needed the same
 * thing, and two of them would have drifted: the token list, the dashed edge that
 * marks a fixed colour, and the keyboard contract are all decisions that have to
 * be made once. A plot's stroke and a drawing's stroke are the same kind of
 * value — a token from one namespace, or a hex the user insisted on — so they get
 * the same control.
 *
 * The full reasoning for each piece lives on the pieces. In short: a listbox
 * rather than a `<select>` because `option` background is drawn by the OS and
 * ignored outright by Safari; `Custom…` because a hex is already legal in the
 * contract; and a dashed swatch edge wherever a fixed colour appears, because
 * the cost of one is paid at a theme switch long after the list has closed.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { isHexColor } from "@qufolio/model";

/**
 * The colours on offer, as **tokens** (D13).
 *
 * `series1`–`series6` lead because that is what the catalog assigns and what an
 * indicator therefore arrives wearing; the semantic pair and the accents follow.
 * No hex appears anywhere in this file — the swatch beside each control resolves
 * its token through the active theme, so the list repaints with everything else.
 */
const COLOR_TOKENS: { id: string; label: string }[] = [
  { id: "series1", label: "Series 1" },
  { id: "series2", label: "Series 2" },
  { id: "series3", label: "Series 3" },
  { id: "series4", label: "Series 4" },
  { id: "series5", label: "Series 5" },
  { id: "series6", label: "Series 6" },
  { id: "positive", label: "Positive" },
  { id: "negative", label: "Negative" },
  { id: "neutral", label: "Neutral" },
  { id: "accent.blue", label: "Blue" },
  { id: "accent.orange", label: "Orange" },
  { id: "accent.teal", label: "Teal" },
  { id: "accent.purple", label: "Purple" },
  { id: "warn", label: "Warn" },
];

/**
 * A token picker whose options **are** the colours.
 *
 * This was a `<select>` with one swatch beside it, and that swatch only ever
 * showed the value already chosen — so picking a colour meant reading
 * "Series 3" and guessing. A native `<select>` cannot fix that: `option`
 * background is ignored outright by Safari and unreliable elsewhere, because the
 * list is drawn by the OS rather than by the page. The only way to put a swatch
 * on every row is to stop using one.
 *
 * What replaces it is the smallest thing that is still a listbox: a button
 * owning the value, a `fixed` panel, roving `aria-activedescendant`, and
 * Escape/arrows/Home/End. Every swatch still resolves through the active theme,
 * so the whole list repaints with the chart rather than holding a colour.
 */
/**
 * The value meaning "no colour at all".
 *
 * The empty string rather than a token, because that is what an **absent**
 * `style.fill` already is once it reaches here — so "none" and "never set" are
 * the same value and the control cannot show one while the drawing has the
 * other.
 */
export const NO_COLOR = "";

/**
 * How "none" is drawn: an outline with nothing in it.
 *
 * Not the panel's background, which would read as white on a light theme —
 * indistinguishable from a genuinely white fill, which is the one thing this
 * option exists to be different from.
 */
const EMPTY_SWATCH: React.CSSProperties = {
  background: "transparent",
  borderStyle: "dashed",
};

export function ColorSelect({
  label,
  value,
  resolve,
  onChange,
  allowNone = false,
}: {
  label: string;
  value: string;
  resolve: (token: string) => string;
  onChange: (token: string) => void;
  /**
   * Offer "None" at the top of the list.
   *
   * Only where an absent colour is a real choice rather than a broken one: a
   * box's *fill* is optional — an unfilled box is an outline, which is a thing
   * people draw on purpose — where a *stroke* of none is an invisible drawing.
   */
  allowNone?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const fixed = isHexColor(value);

  const close = (refocus: boolean) => {
    setOpen(false);
    // Focus returns to the trigger, or the next Tab restarts at the top of the
    // document — the same rule the indicator panel's own popover follows.
    if (refocus) triggerRef.current?.focus();
  };

  return (
    <span style={COLOR_FIELD}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        // Said on the control rather than only in the list, because the cost of
        // a fixed colour is paid later — at the theme switch that leaves this
        // one plot behind — and by then the list is long shut.
        title={fixed ? `${value} — a fixed colour, so this plot will not follow a theme` : label}
        onClick={() => setOpen((o) => !o)}
        style={COLOR_TRIGGER}
      >
        <span
          aria-hidden
          style={{
            ...SWATCH,
            ...(fixed ? FIXED_SWATCH : null),
            // Nothing to resolve, and nothing to paint — a swatch filled with
            // the panel's own background would read as white rather than as
            // absent, so "none" is drawn as an empty outline.
            ...(value === NO_COLOR ? EMPTY_SWATCH : { background: resolve(value) }),
          }}
        />
        <span style={COLOR_TRIGGER_LABEL}>{colorOptionLabel(value)}</span>
        <span aria-hidden style={CARET}>
          ▾
        </span>
      </button>
      {open && (
        <ColorList
          anchorRef={triggerRef}
          label={label}
          value={value}
          resolve={resolve}
          allowNone={allowNone}
          onPick={(token) => {
            onChange(token);
            close(true);
          }}
          // **A custom colour applies and the list stays open.** Picking a token
          // is one decision and closing on it is right; dragging a colour wheel
          // is a dozen, and Chrome fires `change` throughout the drag — so
          // treating it as a pick shut the list on the first movement, before
          // the user had chosen anything. They close it themselves, by clicking
          // away or with the ✕.
          onCustom={onChange}
          onClose={close}
        />
      )}
    </span>
  );
}

/**
 * How one colour is named.
 *
 * A value outside the list — a hex from an older workspace, or a token this
 * build no longer offers — shows as itself rather than snapping to the first
 * entry, which is the promise the `<select>` kept with a trailing `option`.
 */
function colorOptionLabel(value: string): string {
  if (value === NO_COLOR) return "None";
  return COLOR_TOKENS.find((c) => c.id === value)?.label ?? value;
}

/** The options as shown: the built-in list, plus whatever this plot is wearing. */
function colorOptionsFor(
  value: string,
  allowNone: boolean,
): { id: string; label: string }[] {
  const known = COLOR_TOKENS.some((c) => c.id === value) || value === NO_COLOR;
  const listed = known ? COLOR_TOKENS : [...COLOR_TOKENS, { id: value, label: value }];
  // **First, not last.** "None" is the one option somebody scans for when they
  // want to undo a fill, and a list of sixteen colours with it at the bottom is
  // a list they scroll past it in.
  return allowNone ? [{ id: NO_COLOR, label: "None" }, ...listed] : listed;
}

/**
 * The open list. Split out so it mounts and unmounts with the panel — the
 * measuring effect and the document listeners then have exactly the panel's
 * lifetime and need no `open` guard of their own.
 */
function ColorList({
  anchorRef,
  label,
  value,
  resolve,
  onPick,
  onCustom,
  onClose,
  allowNone,
}: {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  label: string;
  value: string;
  resolve: (token: string) => string;
  allowNone: boolean;
  onPick: (token: string) => void;
  /** A custom hex, applied live. Deliberately does not close the list. */
  onCustom: (hex: string) => void;
  onClose: (refocus: boolean) => void;
}) {
  const options = colorOptionsFor(value, allowNone);
  const listId = useId();
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [active, setActive] = useState(() =>
    Math.max(0, options.findIndex((o) => o.id === value)),
  );

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const panel = ref.current;
    if (!anchor || !panel) return;
    const rect = anchor.getBoundingClientRect();
    const { offsetHeight: height, offsetWidth: width } = panel;

    // Below the trigger, flipped above when the room is not there. It opens
    // inside a panel that is already most of the viewport tall, so a list that
    // only ever hung downwards would be cut off on every row past the middle.
    const below = rect.bottom + 2;
    const top =
      below + height <= window.innerHeight - 4 ? below : Math.max(4, rect.top - height - 2);
    setPos({ top, left: Math.max(4, Math.min(rect.left, window.innerWidth - width - 4)) });
  }, [anchorRef]);

  // The list takes focus, not an option: `aria-activedescendant` moves the
  // reader's cursor without moving the DOM's, so arrowing never leaves the
  // element holding the key handler.
  useEffect(() => {
    ref.current?.focus();
  }, []);

  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node;
      // The trigger is not "outside": closing here would let its own click
      // reopen the list, so it could never be shut by the control that opened it.
      if (ref.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose(false);
    };
    // Capture, with the origin checked — a scroll inside this list moves
    // nothing it is positioned against; an ancestor's moves everything. The same
    // trap the indicator panel's popover documents at length.
    const onScroll = (event: Event) => {
      if (ref.current?.contains(event.target as Node)) return;
      onClose(false);
    };
    const onResize = () => onClose(false);
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [anchorRef, onClose]);

  const move = (delta: number) =>
    setActive((i) => Math.min(options.length - 1, Math.max(0, i + delta)));

  return (
    <div
      ref={ref}
      tabIndex={-1}
      aria-label={label}
      aria-activedescendant={`${listId}-${active}`}
      onKeyDown={(event) => {
        switch (event.key) {
          case "ArrowDown":
            move(1);
            break;
          case "ArrowUp":
            move(-1);
            break;
          case "Home":
            setActive(0);
            break;
          case "End":
            setActive(options.length - 1);
            break;
          case "Enter":
          case " ":
            onPick(options[active]!.id);
            break;
          case "Escape":
            onClose(true);
            break;
          case "Tab":
            // Let focus leave, but do not leave a panel hanging over the row it
            // came from.
            onClose(false);
            return;
          default:
            return;
        }
        // Only for the keys handled above. An unhandled key must still reach the
        // page, or Escape would stop closing the panel this one opened inside.
        event.preventDefault();
        event.stopPropagation();
      }}
      style={{
        ...COLOR_LIST,
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        // Placed off the first frame's measurement; hidden rather than
        // undisplayed, which would leave nothing to measure.
        visibility: pos ? "visible" : "hidden",
      }}
    >
      <div role="listbox" aria-label={label}>
        {/* **A ✕, because this list can now outlive a click inside it.** While
          every row closed on selection there was nothing to close: the list was
          gone the moment you chose. A custom colour leaves it open, so it needs a
          way out that is not "guess that clicking the chart works". */}
      <div style={HEAD}>
        <span style={HEAD_LABEL}>{label}</span>
        <button
          type="button"
          aria-label="Close colour list"
          title="Close"
          onClick={() => onClose(true)}
          style={CLOSE}
        >
          ✕
        </button>
      </div>

      {options.map((option, i) => (
          <div
            key={option.id}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={option.id === value}
            onPointerEnter={() => setActive(i)}
            onClick={() => onPick(option.id)}
            style={{
              ...COLOR_OPTION,
              background: i === active ? "var(--qf-row-hover)" : "transparent",
            }}
          >
            <span
              aria-hidden
              style={{
                ...SWATCH,
                ...(isHexColor(option.id) ? FIXED_SWATCH : null),
                ...(option.id === NO_COLOR
                  ? EMPTY_SWATCH
                  : { background: resolve(option.id) }),
              }}
            />
            <span style={{ flex: "1 1 auto" }}>{option.label}</span>
            {option.id === value && <span aria-hidden>✓</span>}
          </div>
        ))}
      </div>

      {/* **The escape hatch, kept below the tokens and marked.**

          A hex is a legal `PlotStyle.stroke` — `resolveColorRef` passes one
          straight through — so this needs no contract change and nothing
          downstream has to learn about it. What it costs is the whole point of
          tokens: a colour picked here does not follow a theme, so one chosen
          against Halloween is very nearly invisible on White. That is shown
          rather than hidden — the swatch of a fixed colour is drawn with a
          dashed edge wherever it appears, and the trigger says so on hover.

          Committed on `change`, unlike the theme editor's swatches, which wait
          for `blur`. There the cost of an intermediate colour is a repaint of
          every surface in the app; here it is one series restyled in place, so
          the live preview is worth more than the frames it costs. */}
      <label style={CUSTOM_ROW}>
        <input
          type="color"
          aria-label={`${label} custom`}
          value={isHexColor(value) ? value : resolve(value)}
          onChange={(e) => onCustom(e.target.value)}
          style={CUSTOM_INPUT}
        />
        <span>Custom…</span>
      </label>
    </div>
  );
}

const COLOR_FIELD: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
};

const SWATCH: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: 2,
  border: "1px solid var(--qf-border)",
};

/** The trigger, sized and bordered like the `<select>` it replaced. */
const COLOR_TRIGGER: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  font: "inherit",
  fontSize: 11,
  padding: "2px 4px",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  cursor: "pointer",
  outline: "none",
};

/**
 * Wide enough for the longest label, so the row does not change width with the
 * colour chosen. A control that resizes as you arrow through it is one whose
 * neighbours move under the pointer.
 */
const COLOR_TRIGGER_LABEL: React.CSSProperties = {
  minWidth: 52,
  textAlign: "left",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const CARET: React.CSSProperties = {
  fontSize: 9,
  color: "var(--qf-text-muted)",
};

/**
 * Above the panel it opens inside (1000): it is a child of that panel in the
 * DOM but escapes its box by being `fixed`.
 */
const COLOR_LIST: React.CSSProperties = {
  position: "fixed",
  zIndex: 1001,
  minWidth: 132,
  maxHeight: 280,
  overflowY: "auto",
  padding: 3,
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
  outline: "none",
};

/**
 * How a fixed colour is marked, everywhere it is drawn.
 *
 * A dashed edge rather than a badge or a different shape: it has to fit in a
 * 10px swatch, sit beside thirteen solid ones without changing the row's
 * rhythm, and still be legible on both a black and a white theme.
 */
const FIXED_SWATCH: React.CSSProperties = {
  borderStyle: "dashed",
  borderColor: "var(--qf-text-muted)",
};

const CUSTOM_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  font: "inherit",
  fontSize: 11,
  padding: "3px 5px",
  marginTop: 3,
  borderTop: "1px solid var(--qf-border)",
  paddingTop: 6,
  cursor: "pointer",
  color: "var(--qf-text-muted)",
};

const CUSTOM_INPUT: React.CSSProperties = {
  width: 14,
  height: 14,
  padding: 0,
  border: "1px solid var(--qf-border)",
  borderRadius: 2,
  background: "transparent",
  cursor: "pointer",
};

const HEAD: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "1px 3px 4px 5px",
  marginBottom: 2,
  borderBottom: "1px solid var(--qf-border)",
};

const HEAD_LABEL: React.CSSProperties = {
  flex: "1 1 auto",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--qf-text-muted)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const CLOSE: React.CSSProperties = {
  font: "inherit",
  fontSize: 11,
  lineHeight: 1,
  padding: "1px 3px",
  border: 0,
  borderRadius: 3,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};

const COLOR_OPTION: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  font: "inherit",
  fontSize: 11,
  padding: "3px 5px",
  borderRadius: 4,
  cursor: "pointer",
  color: "var(--qf-text)",
};
